/**
 * AI polish prompt for per-player tactical briefs.
 *
 * Prompt version: 1.0 — last updated: 2026-05-14
 * Changes from previous: initial version.
 *
 * The rule engine in `@4cm/core/tactics/playerBriefs` produces 4-section
 * briefs that are correct but slightly mechanical. This prompt takes
 * one rule-generated brief + a 1-line tactical context summary, and
 * asks Claude to rewrite each bullet to be punchy and specific (<12
 * words per bullet) while preserving the substantive content.
 *
 * Output is strict JSON so the parse path stays deterministic. Any
 * deviation (markdown wrapper, prose, missing field) is treated as a
 * polish failure and the consumer falls back to the rule output.
 */

import type { PlayerBrief } from '@4cm/core/tactics';

export interface PlayerBriefPolishContext {
  /** Coach's team formation. */
  formation: string;
  /** Opposition formation. */
  oppFormation: string;
  /** Free-text summary of the active team-tactics slot — e.g.
   *  "Short-pass build-up, high press, high line height (75)". */
  teamTacticsSummary: string;
  /** US Soccer phase or age group, for tone calibration. Optional. */
  ageGroup?: string;
}

export const PLAYER_BRIEF_POLISH_SYSTEM = `
You are a tactical analyst writing a punchy, specific brief for a single soccer player.

You will receive:
  - The player's slot (e.g. ST, CDM, LB).
  - The player's named tactical role if any (e.g. false-9, ball-playing CB).
  - A short context block: their team's formation, the opposition formation,
    a one-line tactical-slot summary (build-up + defensive approach + key sliders).
  - A rule-generated brief organised into four sections: in possession, out of
    possession, transitions, set pieces. Each section is 0–3 bullet points.

The substantive content of the rule-generated brief is CORRECT. Your job is to:
  1. Rewrite each bullet so it reads naturally and punchily.
  2. Keep every bullet under 12 words.
  3. Preserve the meaning — do NOT invent new tactical instructions.
  4. Preserve the number of bullets per section (do not merge or split).
  5. If a section is empty, return an empty array — do not invent content.

Output ONLY valid JSON, no markdown fence, no prose:

{
  "inPossession": string[],
  "outOfPossession": string[],
  "transitions": string[],
  "setPieces": string[]
}

If you cannot improve a bullet without changing meaning, return the original wording.
`.trim();

/** Build the user message for one player. Plain text — the system
 *  prompt anchors the schema. */
export function buildPlayerBriefPolishUserContent(
  brief: PlayerBrief,
  ctx: PlayerBriefPolishContext,
): string {
  return [
    `Player slot: ${brief.slot}`,
    `Named role: ${brief.namedRole ?? '—'}`,
    `Team formation: ${ctx.formation}`,
    `Opposition formation: ${ctx.oppFormation}`,
    `Team tactics: ${ctx.teamTacticsSummary}`,
    ctx.ageGroup ? `Age group: ${ctx.ageGroup}` : null,
    '',
    'Rule-generated brief:',
    JSON.stringify(
      {
        inPossession: brief.inPossession,
        outOfPossession: brief.outOfPossession,
        transitions: brief.transitions,
        setPieces: brief.setPieces,
      },
      null,
      2,
    ),
    '',
    'Return the polished brief as JSON only.',
  ]
    .filter((line): line is string => line !== null)
    .join('\n');
}

/** Parser: returns `null` on any deviation from the expected shape. */
export function parsePolishResponse(text: string): {
  inPossession: string[];
  outOfPossession: string[];
  transitions: string[];
  setPieces: string[];
} | null {
  if (!text) return null;
  const trimmed = text.trim();
  // Strip a potential ```json fence the model might add despite the
  // "no markdown fence" instruction.
  const stripped = trimmed
    .replace(/^```(?:json)?\s*/i, '')
    .replace(/```$/i, '')
    .trim();
  let parsed: unknown;
  try {
    parsed = JSON.parse(stripped);
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== 'object') return null;
  const obj = parsed as Record<string, unknown>;
  const arr = (v: unknown): string[] | null =>
    Array.isArray(v) && v.every((s) => typeof s === 'string') ? v : null;
  const inPossession = arr(obj.inPossession);
  const outOfPossession = arr(obj.outOfPossession);
  const transitions = arr(obj.transitions);
  const setPieces = arr(obj.setPieces);
  if (
    inPossession === null ||
    outOfPossession === null ||
    transitions === null ||
    setPieces === null
  ) {
    return null;
  }
  return { inPossession, outOfPossession, transitions, setPieces };
}
